"use client";

import { ScatterChart } from "@/components/charts/scatter-chart";
import { Scatter } from "@/components/charts/scatter";
import { Grid } from "@/components/charts/grid";
import { XAxis } from "@/components/charts/x-axis";
import { YAxis } from "@/components/charts/y-axis";
import { ChartTooltip } from "@/components/charts/tooltip";
import { ReferenceArea } from "@/components/charts/reference-area";
import { ReferenceAreaLabel } from "@/components/charts/reference-area-label";
import { ChartBrush, type ChartBrushSelection } from "@/components/charts/chart-brush";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import {
  ChartAxisFrame,
  ChartXAxisRow,
} from "@/components/charts/chart-axis-frame";
import { ChartLegendHoverProvider } from "@/components/charts/chart-legend-hover";
import { ProjectionFadeOut } from "@/components/charts/bar-projection-context";
import {
  type ChartView,
  ChartViewToggle,
  chartViewToMode,
} from "@/components/charts/projection/chart-view-toggle";
import { MAX_PROJECTED_POINTS } from "@/components/charts/projection/iso-projection";
import { useLegendClickSync } from "@/components/charts/use-legend-click-sync";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
} from "@/components/charts/legend";
import { cn } from "@/lib/utils";
import { ChartThreshold, type ModuleReferenceArea } from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { JSX, useMemo, useState } from "react";
import {
  moduleAxisLabels,
} from "./chart-module-metrics";
import { CHART_PLOT_HEIGHT, MIN_CHART_WIDTH, MIN_PX_PER_POINT } from "@/config/layout";
import { accentChartColors } from "../module-accent";
import { chartPaletteStyle } from "../imports-token-scope";

type ScatterPoint = {
  x: number;
  y: number;
  z?: number;
};

type ScatterSeries = {
  name: string;
  data: ScatterPoint[];
};

type ScatterChartData = {
  series: ScatterSeries[];
};

const DEFAULT_DATA: ScatterChartData = {
  series: [
    {
      name: "School A",
      data: [
        { x: 100, y: 200, z: 200 },
        { x: 120, y: 100, z: 260 },
        { x: 170, y: 300, z: 400 },
        { x: 140, y: 250, z: 280 },
        { x: 150, y: 400, z: 500 },
        { x: 110, y: 280, z: 200 },
      ],
    },
    {
      name: "School B",
      data: [
        { x: 200, y: 260, z: 240 },
        { x: 240, y: 290, z: 220 },
        { x: 190, y: 290, z: 250 },
        { x: 198, y: 250, z: 210 },
        { x: 180, y: 280, z: 260 },
        { x: 210, y: 220, z: 230 },
      ],
    },
  ],
};

const CHART_HEIGHT = CHART_PLOT_HEIGHT.scatter;
const PX_PER_POINT = MIN_PX_PER_POINT.scatter;

export default function ModuleScatterChart({
  data = DEFAULT_DATA,
  xAxisLabel,
  yAxisLabel,
  xAxisUnit,
  yAxisUnit,
  zAxisLabel,
  zAxisUnit,
  zAxisRange,
  thresholds,
  showBrush = false,
  showGrid = true,
  showLegend,
  view3d,
  referenceArea,
  grayscale,
  accent,
  className,
}: {
  data?: ScatterChartData;
  xAxisLabel?: string;
  yAxisLabel?: string;
  xAxisUnit?: string;
  yAxisUnit?: string;
  zAxisLabel?: string;
  zAxisUnit?: string;
  zAxisRange?: [number, number];
  thresholds?: ChartThreshold[];
  showBrush?: boolean;
  showGrid?: boolean;
  showMarkers?: boolean;
  showLegend?: boolean;
  fadeEdges?: boolean;
  view3d?: boolean;
  referenceArea?: ModuleReferenceArea;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const palette = accentChartColors(grayscale, accent);
  const series = data.series ?? [];
  // Bubble size is not plotted yet, so the z fields have no renderer to reach.
  void zAxisLabel;
  void zAxisUnit;
  void zAxisRange;

  const chartData = useMemo(() => {
    const rows = new Map<number, Record<string, number>>();
    for (const [i, s] of series.entries()) {
      const key = `y_${i}`;
      for (const p of s.data) {
        const existing = rows.get(p.x) ?? { x: p.x };
        existing[key] = p.y;
        rows.set(p.x, existing);
      }
    }
    return Array.from(rows.values()).sort(
      (a, b) => (a.x as number) - (b.x as number),
    );
  }, [series]);

  const yThresholds = useMemo(
    () => (thresholds ?? []).filter((t) => t.axis !== "x"),
    [thresholds],
  );

  const [brushSelection, setBrushSelection] =
    useState<ChartBrushSelection | null>(null);

  const visibleChartData = useMemo(() => {
    if (!brushSelection || brushSelection.kind !== "band") return chartData;
    const { startIndex, endIndex } = brushSelection;
    return chartData.slice(startIndex, endIndex + 1);
  }, [chartData, brushSelection]);

  const legendVisible = showLegend ?? series.length > 1;

  const pointCount = series.reduce((sum, s) => sum + (s.data?.length ?? 0), 0);
  const canProject = Boolean(view3d) && pointCount <= MAX_PROJECTED_POINTS;
  const [view, setView] = useState<ChartView>("3d");
  const projection = canProject ? chartViewToMode(view) : undefined;

  const [legendHovered, setLegendHovered] = useState<number | null>(null);
  const {
    activeIndex,
    handleSelect,
    setLabelRef,
    selectedOrigin,
    legendSelectedIndex,
    elementIndex,
    toggleElementIndex,
  } = useLegendClickSync();

  return (
    <ChartLegendHoverProvider
      hoveredIndex={legendHovered}
      onHoverChange={setLegendHovered}
      selectedIndex={activeIndex}
      selectedOrigin={selectedOrigin}
      elementIndex={elementIndex}
      onElementToggle={toggleElementIndex}
    >
    <div className={cn("flex w-full flex-col gap-3", className)} style={chartPaletteStyle(grayscale, accent)}>
      {canProject ? (
        <div className="flex h-8 items-center justify-end">
          <ChartViewToggle
            className="shrink-0"
            kind="scatter"
            onChange={setView}
            value={view}
          />
        </div>
      ) : null}
      <ChartAxisFrame
        {...moduleAxisLabels({ xAxisLabel, yAxisLabel, xAxisUnit, yAxisUnit })}
      >
      <SwipeScroll className="min-w-0 flex-1 pb-1 [mask-image:linear-gradient(to_right,black_0%,black_95%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,black_0%,black_95%,transparent_100%)]">
      <div
        style={{
          minWidth: Math.max(
            MIN_CHART_WIDTH,
            visibleChartData.length * PX_PER_POINT,
          ),
        }}
      >
      <div className="w-full" style={{ height: CHART_HEIGHT }}>
        <ScatterChart
          data={visibleChartData}
          xDataKey="x"
          xScaleKind="linear"
          margin={{ top: 12, right: 24, bottom: 8, left: 0 }}
          aspectRatio="auto"
          className="h-full"
          projection={projection}
        >
          {showGrid && (
            <Grid
              horizontal
              vertical
              highlightRowValues={
                yThresholds.length > 0
                  ? yThresholds.map((t) => t.value)
                  : undefined
              }
              highlightRowStroke={yThresholds[0]?.color ?? (grayscale ? "var(--chart-5)" : "var(--destructive)")}
              highlightRowStrokeDasharray="4,4"
              highlightRowStrokeWidth={1.5}
            />
          )}
          {referenceArea && canProject && (
            <ProjectionFadeOut>
              <ReferenceArea
                y1={referenceArea.y1}
                y2={referenceArea.y2}
                fill={referenceArea.color ?? "var(--chart-1)"}
                fillOpacity={0.15}
                stroke={referenceArea.color ?? "var(--chart-1)"}
                strokeStyle="dashed"
                strokeWidth={1.5}
                fadeEdges
                fadeEdgesLength={12}
                pattern={
                  referenceArea.variant === "pattern"
                    ? (referenceArea.pattern ?? "diagonal")
                    : "none"
                }
              />
              {referenceArea.label && (
                <ReferenceAreaLabel
                  y2={referenceArea.y2}
                  label={stripInlineMarkdown(referenceArea.label)}
                />
              )}
            </ProjectionFadeOut>
          )}
          {referenceArea && !canProject && (
            <>
              <ReferenceArea
                y1={referenceArea.y1}
                y2={referenceArea.y2}
                fill={referenceArea.color ?? "var(--chart-1)"}
                fillOpacity={0.15}
                stroke={referenceArea.color ?? "var(--chart-1)"}
                strokeStyle="dashed"
                strokeWidth={1.5}
                fadeEdges
                fadeEdgesLength={12}
                pattern={
                  referenceArea.variant === "pattern"
                    ? (referenceArea.pattern ?? "diagonal")
                    : "none"
                }
              />
              {referenceArea.label && (
                <ReferenceAreaLabel
                  y2={referenceArea.y2}
                  label={stripInlineMarkdown(referenceArea.label)}
                />
              )}
            </>
          )}
          <YAxis />
          {series.map((s, i) => (
            <Scatter
              key={s.name}
              dataKey={`y_${i}`}
              fill={palette[i % palette.length]}
              label={stripInlineMarkdown(s.name)}
              radius={5}
            />
          ))}
          <XAxis />
          <ChartTooltip
            showDatePill
            rows={(point) =>
              series.map((s, i) => ({
                color: palette[i % palette.length] ?? "#000",
                label: stripInlineMarkdown(s.name),
                value: Number(point[`y_${i}`] ?? 0),
              }))
            }
          />
        </ScatterChart>
      </div>
      <ChartXAxisRow />
      </div>
      </SwipeScroll>
      </ChartAxisFrame>

      {showBrush && chartData.length > 0 && (
        <ChartBrush
          data={chartData as unknown as Record<string, unknown>[]}
          xDataKey="x"
          variant="band"
          height={36}
          onSelectionChange={setBrushSelection}
        />
      )}

      {legendVisible && (
        <SwipeScroll className="w-full pb-1" fade="auto">
          <Legend
            items={series.map((s, i) => ({
              label: stripInlineMarkdown(s.name),
              value: s.data.length,
              color: palette[i % palette.length] ?? "#000",
            }))}
            hoveredIndex={legendHovered}
            onHoverChange={setLegendHovered}
            selectedIndex={legendSelectedIndex}
            onSelect={handleSelect}
            onLabelRef={setLabelRef}
            className="flex flex-row gap-6"
          >
            <LegendItem className="flex shrink-0 items-center gap-2">
              <LegendMarker className="h-2.5 w-2.5" />
              <LegendLabel />
            </LegendItem>
          </Legend>
        </SwipeScroll>
      )}
    </div>
    </ChartLegendHoverProvider>
  );
}
